import React, { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { Subject } from 'rxjs';
import useStyles from 'isomorphic-style-loader/useStyles';

import { SlLEContext } from '../../private/global-constants';
import { Loader } from '../../private/components/Loader/Loader';
import { NavigationBar } from '../../private/components/NavigationBar/NavigationBar';
import { SlActionsService } from '../../private/services/ActionsService/sl-actions.service';
import { SlActions } from '../../private/services/ActionsService/sl-actions';
import { ActionType, RequestType } from '../../private/global-interface';
import { RouteWithCourseContainer } from '../../private/containers/RouteWithCourseContainer/RouteWithCourseContainer';
import { RouteWithLearningPathContainer } from '../../private/containers/RouteWithLearningPathContainer/RouteWithLearningPathContainer';
import { FlashMessage } from '../../private/components/FlashMessage/FlashMessage';
import { CourseNotFound } from '../../private/components/ErrorPages/CourseNotFound/CourseNotFound';
import { LessonNotFound } from '../../private/components/ErrorPages/LessonNotFound/LessonNotFound';
import { NotAllowedFeature } from '../../private/components/NotAllowedFeature/NotAllowedFeature';
import { Modals } from '../../private/components/Modals/Modals';
import { useComponentWillMount } from '../../../shared/public/SlUtils/useComponentWillMount';

import s from './SlLearnEngine.scss';
import { Container } from '../../../symphony';
import { getEnvironment } from '../../../../client/utils/environment';
import { SlAICourses } from '../../../_aicourses/public/SlAICourses';
import { LECourses } from '../LECourses/LECourses';

export interface SlLearnEngineProps {
  configs: {
    soloLearnUrl: string;
  };
  isAiSite: boolean;
}

export function SlLearnEngine({ configs, isAiSite }: SlLearnEngineProps): JSX.Element {
  useStyles(s);
  const [context, setContext] = useState<'coding' | 'ai'>(isAiSite ? 'ai' : 'coding');
  const unsubscriber = new Subject<void>();

  useComponentWillMount(() => {
    Container.register(SlLEContext, 'unsubscriber', unsubscriber);
    Container.register(SlLEContext, 'soloLearnUrl', configs.soloLearnUrl);
    Container.take(SlLEContext, SlActionsService);
  });

  useEffect(() => {
    const { actions$ } = Container.take(SlLEContext, SlActions);

    actions$.next({
      type: ActionType.OnSlLearnEngineMount,
      requestType: RequestType.Parallel,
    });

    if (typeof window !== 'undefined') {
      const environment = getEnvironment();
      if (environment.includes('ai')) {
        setContext('ai');
      } else {
        setContext('coding');
      }
    } else {
      setContext(isAiSite ? 'ai' : 'coding');
    }

    return () => {
      actions$.next({
        type: ActionType.OnSlLearnEngineUnmount,
        requestType: RequestType.Parallel,
      });

      unsubscriber.next();
      unsubscriber.complete();
      Container.reset(SlLEContext);
    };
  }, []);

  return (
    <main className="sl-learn-engine">
      <Routes>
        <Route path="/" element={context === 'ai' ? <SlAICourses /> : <LECourses />} />
        <Route
          path="courses/:courseAlias/*"
          element={<RouteWithCourseContainer />}
        />
        <Route
          path="learning-paths/:pathAlias/*"
          element={<RouteWithLearningPathContainer />}
        />
        <Route path="/not-found-course" element={<CourseNotFound />} />
        <Route path="/not-found-lesson" element={<LessonNotFound />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <NavigationBar />
      <Loader />
      <FlashMessage />
      <Modals />
      <NotAllowedFeature />
    </main>
  );
}
